import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CheckCircle2 } from 'lucide-react';

const CianoGourmet = () => {
  const formats = [
    'Interactive Food Stations (sweet + savory)',
    'Brunch Stations & Elevated Coffee Breaks',
    'Mini formats + tasting-style experiences',
    'Private dinners and curated buffets',
  ];

  const knownFor = [
    'Interactive stations with elevated presentation',
    'Hospitality-driven service flow (timing, replenishment, guest experience)',
    'Menus that feel curated—not catered',
    'Execution that looks effortless',
  ];

  const idealFor = [
    'Private celebrations with discerning hosts',
    'Corporate receptions and executive gatherings',
    'Brand activations and cultural moments',
    'Milestone events that require premium standards',
  ];

  const process = [
    { step: '1', title: 'Inquiry + date confirmation', desc: 'Share your vision and preferred date' },
    { step: '2', title: 'Vision, guest count, and service style', desc: 'Define the experience' },
    { step: '3', title: 'Menu + proposal', desc: 'Custom proposal and pricing' },
    { step: '4', title: 'Production + staffing plan', desc: 'Behind-the-scenes coordination' },
    { step: '5', title: 'Event execution + wrap', desc: 'Flawless delivery on the day' },
  ];

  const faqs = [
    {
      q: 'How far in advance should we inquire?',
      a: 'Lead times vary by season—reach out early for preferred dates.',
    },
    {
      q: 'Do you offer tastings?',
      a: 'Available for qualified bookings and specific formats.',
    },
    {
      q: 'Do you provide staff and rentals?',
      a: 'Staffing can be included; rentals depend on event needs and venue requirements.',
    },
  ];

  return (
    <div className="bg-white pt-32 pb-20">
      {/* Hero with Image */}
      <section className="relative">
        {/* Hero Image */}
        <div className="w-full h-[60vh] md:h-[70vh] overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1680342630889-b475e612a058?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjYXRlcmluZ3xlbnwwfHx8fDE3NzIwNjUxNjl8MA&ixlib=rb-4.1.0&q=85"
            alt="Ciano Gourmet Signature Station"
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Hero Content Overlay */}
        <div className="section-padding px-6 bg-vainilla">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-serif text-5xl md:text-7xl font-bold text-stone-900 mb-6">
              Ciano Gourmet
            </h1>
            <p className="text-2xl text-stone-600 mb-6">
              Premium catering meets experiential excellence.
            </p>
            <p className="text-lg text-stone-600 mb-10 leading-relaxed">
              Founded in 2015 in the Dominican Republic. Elevated culinary experiences designed with intention, taste, and flawless execution.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-stone-900 text-white hover:bg-stone-800 px-8 py-6 text-base">
                  Request Availability (Dominican Republic)
                </Button>
              </Link>
              <Button variant="outline" className="border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white px-8 py-6 text-base">
                View Signature Formats
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What We're Known For */}
      <section className="section-padding px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-8">What We're Known For</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {knownFor.map((item, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-terracota flex-shrink-0 mt-1" />
                <p className="text-stone-600">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-8">Ideal For</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {idealFor.map((item, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <span className="w-2 h-2 bg-terracota rounded-full mt-2 flex-shrink-0"></span>
                <p className="text-stone-600">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Signature Formats */}
      <section className="section-padding px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-8">Signature Formats</h2>
          
          {/* Image Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
            <div className="aspect-[4/5] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1680342627018-1cfc75d0597a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXRlcmluZ3xlbnwwfHx8fDE3NzIwNjUxNjl8MA&ixlib=rb-4.1.0&q=85" alt="Interactive Stations" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </div>
            <div className="aspect-[4/5] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1605880319307-757c2d258a3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBjYXRlcmluZ3xlbnwwfHx8fDE3NzIwNjUxNjl8MA&ixlib=rb-4.1.0&q=85" alt="Coffee Break" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </div>
            <div className="aspect-[4/5] overflow-hidden">
              <img src="https://images.pexels.com/photos/114974/pexels-photo-114974.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Mini Formats" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </div>
            <div className="aspect-[4/5] overflow-hidden">
              <img src="https://images.pexels.com/photos/34298919/pexels-photo-34298919.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Plated Moment" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </div>
            <div className="aspect-[4/5] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1751651054934-3fbdf1d54d8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjYXRlcmluZ3xlbnwwfHx8fDE3NzIwNjUxNzd8MA&ixlib=rb-4.1.0&q=85" alt="Brunch Station" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </div>
            <div className="aspect-[4/5] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1751651054945-882d49cbdbfc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjYXRlcmluZ3xlbnwwfHx8fDE3NzIwNjUxNzd8MA&ixlib=rb-4.1.0&q=85" alt="Private Dinner" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </div>
          </div>
          
          {/* Format List */}
          <div className="grid md:grid-cols-2 gap-6">
            {formats.map((format, idx) => (
              <Card key={idx} className="border-perla bg-vainilla">
                <CardContent className="p-6">
                  <p className="text-stone-900 font-medium">{format}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-12 text-center">How It Works</h2>
          <div className="space-y-6">
            {process.map((item, idx) => (
              <div key={idx} className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-stone-900 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-serif text-xl font-bold text-stone-900 mb-1">{item.title}</h3>
                  <p className="text-stone-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Note */}
      <section className="px-6 py-12 bg-stone-900 text-white">
        <div className="max-w-5xl mx-auto text-center">
          <h3 className="font-serif text-2xl font-bold mb-3">A Note on Location</h3>
          <p className="text-lg opacity-90">Based in the Dominican Republic. Serving events nationwide.</p>
        </div>
      </section>

      {/* Proof Carousel */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-12 text-center">Recent Events</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="aspect-[4/5] overflow-hidden mb-4">
                <img src="https://images.pexels.com/photos/34890767/pexels-photo-34890767.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Corporate Reception" className="w-full h-full object-cover" />
              </div>
              <p className="text-sm text-stone-600 text-center">Corporate reception for 150 guests featuring interactive seafood and charcuterie stations</p>
            </div>
            <div>
              <div className="aspect-[4/5] overflow-hidden mb-4">
                <img src="https://images.unsplash.com/photo-1751651054985-7dc01c37369c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjYXRlcmluZ3xlbnwwfHx8fDE3NzIwNjUxNzd8MA&ixlib=rb-4.1.0&q=85" alt="Private Celebration" className="w-full h-full object-cover" />
              </div>
              <p className="text-sm text-stone-600 text-center">Private milestone celebration with curated buffet and plated service for 80 guests</p>
            </div>
            <div>
              <div className="aspect-[4/5] overflow-hidden mb-4">
                <img src="https://images.pexels.com/photos/34890756/pexels-photo-34890756.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Brand Activation" className="w-full h-full object-cover" />
              </div>
              <p className="text-sm text-stone-600 text-center">Brand activation featuring mini tasting experiences and artisanal beverage pairings</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-12 text-center">FAQ</h2>
          <div className="space-y-8">
            {faqs.map((faq, idx) => (
              <div key={idx}>
                <h3 className="font-semibold text-lg text-stone-900 mb-2">{faq.q}</h3>
                <p className="text-stone-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-stone-900 mb-6">
            Ready to design a culinary experience your guests won't forget?
          </h2>
          <p className="text-stone-600 mb-8">Response within 24–48 hours</p>
          <Link to="/contact">
            <Button className="bg-stone-900 text-white hover:bg-stone-800 px-12 py-6 text-lg font-semibold">
              Request Availability
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CianoGourmet;